import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Typography, Accordion, AccordionSummary, AccordionDetails, useMediaQuery, } from "@mui/material";
import { DocumentScanner, ArrowDownward } from '@mui/icons-material';

import { useBoardManageItemQuery } from "../../hooks/useBoard/useBoardManageQuery";
import { useBoardItemListFaqQuery } from "../../hooks/useBoard/useBoardQuery";

import ContentLayout from "../../layouts/ContentLayout";
import { ControlCircularProgressBar } from "../../components/board";

/*
    2025.04.02
    FAQ
    게시판 정보
    ManageId, BoardName, BoardType, NewYn, NewTerm, CommentYn, WriteRole, ReadRole, OrdYn, CreatedDate, CreatedBy
    게시글 정보
    BoardId, BoardTitle, BoardType, Content, ReplyBody, BoardHit, ManageId, BoardOrd, CreatedDate, CreateByName, CreatedBy
    Comment 정보
    CommentId, BoardId, CreatedByName, CommentBody, CreatedDate, CreatedBy

    CreatedBy 정보와 사용자 계정 정보 같으면 수정 및 삭제 가능 (FAQ 게시판을 제외한 게시판의 경우 적용된다.)
    FAQ는 테이블 목록형이 아니고 카드 형식의 아이템으로 구성한다.
    게시된 항목의 수정 및 삭제 기능이 존재하지 않는다. 작성 기능도 없다.
    디자인 형식에 따라서 제목 및 내용 또는 작성일 정도로 콘텐트를 구성한다.
*/


export default function FaqViews () 
{
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [ pageTitle, setPageTitle ] = React.useState(t('nav.board1'));
    const defaultBoardInfo = {ManageId: process.env.REACT_APP_BOARD_FAQ_MANAGE_ID, BoardName: "", BoardType:"board", NewYn: "N", NewTerm: "7", CommentYn: "N", WriteRole: "system", ReadRole: "system", OrdYn: "N" };
    const [ boardInfo, setBoardInfo] = React.useState(defaultBoardInfo);
    const { error: boardInfoError, data: boardInfoData } = useBoardManageItemQuery(process.env.REACT_APP_BOARD_FAQ_MANAGE_ID);
    const iconType = boardInfo.BoardType === "faq" ? <ArrowDownward /> : <DocumentScanner />;

    const defaultQueryOptions =  {
        ManageId: process.env.REACT_APP_BOARD_FAQ_MANAGE_ID,
        Page: 1,
        PageSize: 1000,
        BoardTitle: "",
        Content: "",
    };
    const [queryOptions, setQueryOptions] = React.useState(defaultQueryOptions);   
    const { isError, isLoading, isFetching, isSuccess, error, data: faqData } = useBoardItemListFaqQuery(queryOptions);

    const [ dataSet, setDataSet ] = React.useState({
        records: [],
        rowLength: 0,
        editable: false,
        isLoading: false,
    });
    const boardType = boardInfo.BoardType ?? "faq";
    const [expanded, setExpanded] = React.useState(false);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };
    
    React.useEffect(() => {
        document.title = 'BWOB - ' + pageTitle;
        try {
            if(boardInfoData !== undefined && boardInfoData !== null)
            {
                if(boardInfoData.data.BoardType !== "faq")
                {
                    navigate("/");
                }
                setBoardInfo(boardInfoData.data);
            }
        }
        catch(err)
        {
            console.log('Access Deny', new Date().toISOString(), boardType, boardInfoData, err);
        }

        return () => {
            setBoardInfo(defaultBoardInfo);
        } 
    }, [boardInfoError, boardInfoData]);
        
    React.useEffect(() => {
        try {
            if(faqData !== undefined && faqData !== null)
            {
                setDataSet({...dataSet, records: faqData.data.Results, rowLength: faqData.data.TotalCnt, isLoading: false});
            }
        }
        catch(err)
        {
            console.log('No Data', new Date().toISOString(), boardType, queryOptions, err);
        }

        return () => {
        } 
    }, [isError, isLoading, isFetching, isSuccess, error, faqData]);


    return(
        <ContentLayout pageTitle={pageTitle} pageDescription={t('nav.board1_description')} addClassName="content-layout-sub2">

            {/* 데이타 영역 */}
            <div style={{ marginTop: "20px", paddingRight: isMobile ? "10px" : "20px", paddingLeft: isMobile ? "10px" : "20px" }}>
                <Container className="page-content" sx={{ width: '100%', height: '100%', alignItems: 'center'}}>
                {
                    dataSet.records.map((row, index) => (
                        <Accordion key={`panel1_${index}_${row.RowNum}`} sx={{ width: '100%', mb: 1,  boxShadow: '0', backgroundColor: 'transparent', '&::before': { backgroundColor: 'transparent' } }} expanded={expanded === `panel1_${index}_${row.RowNum}`}  onChange={handleChange(`panel1_${index}_${row.RowNum}`)}>
                            <AccordionSummary
                                expandIcon={iconType}
                                aria-controls={`panel1_${index}_${row.RowNum}-content`}
                                id={`panel1_${index}_${row.RowNum}-header`}
                                sx={{ backgroundColor: "#F0E8E5",  boxShadow: '0', borderRadius: 1, border: "1px solid #D8C9C4", "&.Mui-expanded": { backgroundColor: "#D8C9C4" } }}            
                            >
                                <Typography sx={{ fontWeight: 700 }}>[{row.RowNum.toString().padStart(3, '0')}] &nbsp;</Typography> 
                                <Typography sx={{ fontWeight: 600 }}>{row.BoardTitle}</Typography>
                            </AccordionSummary>
                            <AccordionDetails className="content-layout-text" sx={{ backgroundColor: "#F0E8E5", boxShadow: '0', borderRadius: 1, border: "1px solid #D8C9C4", "&.Mui-expanded": { backgroundColor: "#D8C9C4" } }}>
                                {/* <Divider sx={{ mt: 0, mb: 1, borderStyle: "dotted" }} /> */}
                                <div style={{whiteSpace: "pre-wrap"}}>
                                    {row.Content.split('\n').map((item, idx) => {
                                        return (
                                            <Typography key={`${idx}_${item}`}>&nbsp; {item}</Typography>
                                        )
                                    })
                                    }
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    ))
                }
                </Container>
            </div>
            
            <ControlCircularProgressBar initialTime={1} />
            <Typography>&nbsp;</Typography>

        </ContentLayout>
    );

};
